import { useState, useEffect } from 'react';
import { useRelatedProducts } from '@algolia/recommend-react';
import { localizedProductUrl } from '@utils/url-helpers';
import { PRODUCT_TYPES } from '@utils/enums';
import recommend from '@algolia/recommend';
import * as Sentry from '@sentry/react';

const recommendClient = recommend(process.env.ALGOLIA_APP_ID, process.env.ALGOLIA_RECOMMEND_API_KEY);
const indexName = 'shopify_products';

// these values are hard coded –– performance hit from fetching metafields is not worth it for 5 products
export const getAccessoryCaption = (title, variantTitle) => {
	switch (title) {
		case 'Cleaning Cloths':
			return ' Pack of 3 eco-friendly cloths ';

		case 'Cleaning Kit':
			return ' The all-in-one kit includes lens cleaning spray, a cloth, & a carrying kit ';

		case 'Glasses Case':
			return ' Holds a Base Frame & two Top Frames ';

		case 'Handy Dandy Cleaning Kit':
			return ' The all-in-one kit includes lens cleaning spray, a cloth, & a carrying kit';

		case 'Top Frame Case':
			return `in ${variantTitle} - holds two Top Frames`;

		case 'Wall Hanger':
			return `${variantTitle === 'Perry' ? 'featuring Perry, our mascot' : `in ${variantTitle}`}`;

		default:
			return `in ${variantTitle}`;
	}
};

const useAlgoliaRecommendations = ({ objectID }) => {
	const [recommendedProducts, setRecommendedProducts] = useState([]);

	const { recommendations, status } = useRelatedProducts({
		recommendClient,
		indexName,
		objectIDs: [objectID],
	});

	const generateCaption = item => {
		if (item.product_type === PRODUCT_TYPES.ACCESSORY) {
			return getAccessoryCaption(item.title, item.option1);
		}
		if (item.product_type === PRODUCT_TYPES.BASE_FRAME) {
			return `in ${item.option1}`;
		}
		return `for The ${item.option1}`;
	};

	const processAlgoliaRecommendations = items => {
		return items.reduce((result, item) => {
			if (item.inventory_available) {
				result.push({
					caption: generateCaption(item),
					frame_shape: item.option1,
					handle: item.handle,
					id: parseInt(item.objectID, 10),
					image: item.image,
					option1: item.option1,
					price: item.price * 100,
					product_id: item.id,
					title: item.title,
					type: item.product_type,
					url: localizedProductUrl(item.handle),
					available: item.inventory_available,
				});
			}
			return result;
		}, []);
	};

	const updateRecommendations = recs => {
		const processedRecs = processAlgoliaRecommendations(recs);
		setRecommendedProducts(processedRecs);
	};

	useEffect(() => {
		updateRecommendations(recommendations);
	}, [recommendations]);

	useEffect(() => {
		const timeout = setTimeout(() => {
			if (status !== 'idle') {
				const errorMessage = `There seems to be a problem while fetching algolia recommendations for product id: ${objectID}. Last status captured is ${status}`;
				console.error(errorMessage);
				Sentry.captureException(Error(errorMessage));
			}
		}, 3000);

		return () => {
			clearTimeout(timeout);
		};
	}, [status]);

	return recommendedProducts;
};

export default useAlgoliaRecommendations;
